// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Skyhold — the three modes. A mode must change how the game PLAYS, not just a number, so the
// spread here is: the pure duel (keep), an asymmetric charge economy (charter), and a board you
// cannot cover (highlands, where the fork and the abandoned tower become real verbs).
//
// The HOST's pick travels frozen inside rematch.ts's round start; everything else — board size,
// builder count, terrain, the power row — is DERIVED from mode + seed on every peer, so a mode can
// never desync. modeOf() guards an id off the wire with Object.hasOwn (a bare `MODES[id] ||`
// lets 'constructor' through as a Mode of undefined fields).

export interface Mode {
  id: string;
  name: string;
  blurb: string;
  /** Board width/height in cells. */
  w: number;
  h: number;
  /** Builders per side. */
  builders: number;
  /** Charter only: each side drafts one power tile. */
  powers: boolean;
  /** Seeded starting terrain, as 180°-symmetric PAIRS: [level-1 pairs, level-2 pairs]. */
  terrain: [number, number];
}

export const MODES: Record<string, Mode> = {
  keep: {
    id: 'keep',
    name: 'Keep',
    blurb: '5×5, two builders each. The pure duel — every threat is answerable, so it is all tempo.',
    w: 5,
    h: 5,
    builders: 2,
    powers: false,
    terrain: [2, 1],
  },
  charter: {
    id: 'charter',
    name: 'Charter',
    blurb: 'Each side drafts one power tile with three charges. Blocking is no longer enough.',
    w: 5,
    h: 5,
    builders: 2,
    powers: true,
    terrain: [2, 1],
  },
  highlands: {
    id: 'highlands',
    name: 'Highlands',
    blurb: '6×6, three builders each. Too much board to cover — forks open and towers get abandoned.',
    w: 6,
    h: 6,
    builders: 3,
    powers: false,
    terrain: [3, 1],
  },
};

export const MODE_IDS: string[] = ['keep', 'charter', 'highlands'];
export const DEFAULT_MODE = 'keep';

export function modeOf(id: string | null | undefined): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) return MODES[id];
  return MODES[DEFAULT_MODE];
}
